import { useMemo } from 'react';
import type { DashboardPreset } from '@/entities/dashboards/model/api/types';
import { allocateSlug, appGeneratePath, type RoutePath } from '@/shared/lib';
import { useDashboardPresets } from './useDashboardPresets';

/**
 * Шаблон адреса раздела закреплённого дашборда-пресета — единственное место, где он задан.
 * Записи у дашборда нет, поэтому адрес состоит из одного слага.
 */
export const DASHBOARD_PRESET_ROUTE_PATH = '/dashboard-preset/:slug' satisfies RoutePath;

/** Слаг пресета, из названия которого не выходит ни одного адресного символа */
const FALLBACK_SLUG = 'dashboard';

/** Закреплённый дашборд-пресет вместе с адресом его раздела */
export type PinnedDashboardPresetRoute = {
  /** Сам закреплённый пресет */
  preset: DashboardPreset;
  /** Слаг пресета — транслитерация названия, различающаяся у закреплённых пресетов */
  slug: string;
  /** Полный адрес раздела пресета */
  path: string;
};

/**
 * Хук адресов закреплённых дашбордов-пресетов: по пункту меню на каждый и по адресу,
 * который открывает страницу дашбордов с этим пресетом.
 *
 * Слаг считается от текущего названия, поэтому переименование меняет и адрес; совпадения
 * названий разводит {@link allocateSlug}.
 */
export const usePinnedDashboardPresetRoutes = (): PinnedDashboardPresetRoute[] => {
  const { presets } = useDashboardPresets();
  return useMemo(() => {
    const usedSlugs = new Set<string>();
    return presets
      .filter((preset) => preset.pinned)
      .map((preset) => {
        const slug = allocateSlug(preset.name, usedSlugs, FALLBACK_SLUG);
        return { preset, slug, path: appGeneratePath(DASHBOARD_PRESET_ROUTE_PATH, { slug }) };
      });
  }, [presets]);
};
